import { useEffect, useRef, useState, type ComponentType, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { ArrowUp, Bot, EyeOff, Expand, Maximize2, Mic, Minimize2, RotateCcw, Sparkles, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useCopilotDictation } from "@/hooks/use-copilot-dictation";

const DEFAULT_MAX_CHARS = 2000;

type PanelSize = "compact" | "expanded" | "maximized";

export interface AssistantSuggestion {
  label: string;
  icon?: ComponentType<{ className?: string }>;
  onSelect: () => void;
}

export interface GlowingAiChatAssistantProps {
  enabled?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  message?: string;
  onMessageChange?: (value: string) => void;
  onSend?: (message: string) => void;
  /** Shown under the input when nothing more urgent (voice, length) is. */
  disclaimer?: string;
  onNewChat?: () => void;
  loading?: boolean;
  title?: string;
  statusLabel?: string;
  placeholder?: string;
  maxChars?: number;
  /** Conversation; shown once the user has asked something. */
  children?: ReactNode;
  /** Welcome text shown on the start screen (before the first question). */
  intro?: ReactNode;
  /** Show the start screen instead of the conversation. */
  showIntro?: boolean;
  suggestions?: AssistantSuggestion[];
  className?: string;
  onDisable?: () => void;
}

function RoundButton({
  label,
  onClick,
  active,
  children,
}: {
  label: string;
  onClick: () => void;
  active?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className={cn(
        "flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border/70 bg-card text-muted-foreground shadow-xs transition-colors hover:bg-muted hover:text-foreground",
        active && "border-primary/30 bg-primary/10 text-primary",
      )}
    >
      {children}
    </button>
  );
}

export function GlowingAiChatAssistant({
  enabled = true,
  open: controlledOpen,
  onOpenChange,
  message: controlledMessage,
  onMessageChange,
  onSend,
  disclaimer,
  onNewChat,
  loading = false,
  title = "Clinical AI Assistant",
  statusLabel = "Ready",
  placeholder = "Type a message…",
  maxChars = DEFAULT_MAX_CHARS,
  children,
  intro,
  showIntro = false,
  suggestions = [],
  className,
  onDisable,
}: GlowingAiChatAssistantProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [internalMessage, setInternalMessage] = useState("");
  const [panelSize, setPanelSize] = useState<PanelSize>("compact");
  const [mounted, setMounted] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isOpen = controlledOpen ?? internalOpen;
  const message = controlledMessage ?? internalMessage;
  const charCount = message.length;
  const messageRef = useRef(message);
  messageRef.current = message;

  const setOpen = (next: boolean) => {
    if (controlledOpen === undefined) setInternalOpen(next);
    onOpenChange?.(next);
    if (!next) setPanelSize("compact");
  };

  const setMessage = (value: string) => {
    const trimmed = value.slice(0, maxChars);
    if (controlledMessage === undefined) setInternalMessage(trimmed);
    onMessageChange?.(trimmed);
  };

  const handleSend = () => {
    const text = message.trim();
    if (!text || loading) return;
    onSend?.(text);
    if (controlledMessage === undefined) setInternalMessage("");
    onMessageChange?.("");
  };

  // Enter sends; Shift+Enter adds a new line.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSend();
    }
  };

  const appendVoiceText = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    const base = messageRef.current.trim();
    setMessage(base ? `${base} ${trimmed}` : trimmed);
  };
  const { listening: voiceListening, error: voiceError, toggle: toggleVoice, stop: stopVoice, supported: voiceSupported } =
    useCopilotDictation(appendVoiceText);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (chatRef.current?.contains(target)) return;
      if (target.closest("[data-glowing-ai-fab]")) return;
      if (target.closest("[data-staff-chat-fab]")) return;
      if (target.closest("[aria-label='Staff chat']")) return;
      setOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) textareaRef.current?.focus();
  }, [isOpen, panelSize]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (panelSize === "maximized") setPanelSize("expanded");
      else if (panelSize === "expanded") setPanelSize("compact");
      else setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, panelSize]);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) stopVoice();
  }, [isOpen, stopVoice]);

  if (!enabled) return null;

  const backdrop =
    isOpen && mounted
      ? createPortal(
          <button
            type="button"
            aria-label="Close assistant"
            className="glowing-ai-backdrop fixed inset-0 z-[88] animate-in fade-in duration-200"
            onClick={() => setOpen(false)}
          />,
          document.body,
        )
      : null;

  const sizeLabel =
    panelSize === "compact" ? "Expand panel" : panelSize === "maximized" ? "Shrink to large panel" : "Collapse panel";
  const footerNote = voiceListening
    ? "Listening…"
    : voiceError
      ? voiceError
      : charCount > maxChars * 0.9
        ? `${charCount}/${maxChars} characters`
        : disclaimer ?? null;

  return (
    <>
      {backdrop}

      <div className={cn("relative shrink-0", className)}>
        <button
          type="button"
          data-glowing-ai-fab
          aria-label={isOpen ? "Close AI assistant" : "Open AI assistant"}
          aria-expanded={isOpen}
          onClick={() => setOpen(!isOpen)}
          className={cn(
            "glowing-ai-fab relative z-[91] flex h-14 w-14 items-center justify-center rounded-full border-2 border-primary-foreground/25 text-primary-foreground shadow-xl transition-all duration-500",
            isOpen ? "rotate-90 scale-95" : "rotate-0",
          )}
        >
          <div className="absolute inset-0 rounded-full bg-gradient-to-b from-white/25 to-transparent opacity-50 dark:from-card" />
          <div className="relative z-10">
            {isOpen ? <X className="h-6 w-6" /> : <Bot className="h-7 w-7" />}
          </div>
          {!isOpen && <div className="absolute inset-0 rounded-full bg-primary/30 animate-ping opacity-20" />}
        </button>

        {isOpen && (
          <div
            ref={chatRef}
            className={cn(
              "glowing-ai-pop-in z-[90] flex flex-col origin-bottom-right text-foreground transition-all duration-300 ease-out",
              panelSize === "maximized"
                ? "fixed inset-2 sm:inset-3"
                : panelSize === "expanded"
                  ? "fixed bottom-4 right-4 left-4 top-4 sm:left-auto sm:w-[min(94vw,960px)] sm:top-6 sm:bottom-6 sm:right-6"
                  : "absolute bottom-[calc(100%+0.75rem)] right-0 w-[min(100vw-2.5rem,420px)]",
            )}
          >
            <div
              className={cn(
                "flex flex-col overflow-hidden rounded-[28px] border border-border/60 bg-card p-3 shadow-2xl",
                panelSize === "compact" ? "h-[min(78vh,620px)]" : "h-full",
              )}
            >
              {/* Header: title pill + round controls */}
              <div className="flex shrink-0 items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2.5 rounded-full border border-border/70 bg-card py-1.5 pl-1.5 pr-4 shadow-xs">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-zinc-900 text-primary dark:bg-zinc-800">
                    <Sparkles className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 leading-tight">
                    <p className="truncate text-sm font-semibold text-foreground">{title}</p>
                    <p className="truncate text-[11px] font-medium text-muted-foreground">{statusLabel}</p>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {onNewChat && (
                    <RoundButton label="New chat" onClick={onNewChat}>
                      <RotateCcw className="h-4 w-4" />
                    </RoundButton>
                  )}
                  <RoundButton
                    label={sizeLabel}
                    onClick={() => setPanelSize((s) => (s === "compact" || s === "maximized" ? "expanded" : "compact"))}
                  >
                    {panelSize === "compact" ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
                  </RoundButton>
                  {panelSize !== "compact" && (
                    <RoundButton
                      label={panelSize === "maximized" ? "Exit full screen" : "Full screen"}
                      active={panelSize === "maximized"}
                      onClick={() => setPanelSize((s) => (s === "maximized" ? "expanded" : "maximized"))}
                    >
                      <Expand className="h-4 w-4" />
                    </RoundButton>
                  )}
                  {onDisable && (
                    <RoundButton label="Hide assistant" onClick={onDisable}>
                      <EyeOff className="h-4 w-4" />
                    </RoundButton>
                  )}
                  <RoundButton label="Close" onClick={() => setOpen(false)}>
                    <X className="h-4 w-4" />
                  </RoundButton>
                </div>
              </div>

              {/* Body: start screen or conversation */}
              <div className="glowing-ai-messages mt-3 min-h-0 flex-1 overflow-y-auto rounded-3xl bg-muted/70 dark:bg-muted/40">
                {showIntro ? (
                  <div className="flex min-h-full flex-col items-center justify-center gap-6 px-6 py-10 text-center">
                    <div className="relative h-23 w-25" aria-hidden>
                      <span className="absolute inset-x-1.5 bottom-0 top-3 rounded-2xl bg-card shadow-md" />
                      <span className="absolute inset-x-0 bottom-2 top-0 flex items-center justify-center rounded-2xl bg-zinc-900 text-primary shadow-lg dark:bg-zinc-800">
                        <Sparkles className="h-8 w-8" />
                      </span>
                    </div>
                    <div className="max-w-77.5 text-[15px] leading-relaxed text-muted-foreground">{intro}</div>
                    {suggestions.length > 0 && (
                      <div className="flex flex-wrap justify-center gap-2.5">
                        {suggestions.map((s, i) => {
                          const Icon = s.icon;
                          return (
                            <button
                              key={s.label}
                              type="button"
                              disabled={loading}
                              onClick={s.onSelect}
                              className={cn(
                                "flex items-center gap-1.5 rounded-full px-4 py-2.5 text-[13px] font-semibold transition-all active:scale-95 disabled:opacity-50",
                                i === 0
                                  ? "bg-zinc-900 text-white hover:bg-zinc-800 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100"
                                  : "border border-border/70 bg-card text-foreground shadow-xs hover:bg-muted",
                              )}
                            >
                              {Icon && <Icon className="h-4 w-4" />}
                              {s.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="px-3 py-3">{children}</div>
                )}
              </div>

              {/* Composer */}
              <div className="flex shrink-0 items-end gap-2 pl-2 pt-3">
                <textarea
                  ref={textareaRef}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={loading}
                  rows={1}
                  placeholder={placeholder}
                  aria-label="Message the assistant"
                  className="field-sizing-content max-h-32 min-h-12 flex-1 resize-none bg-transparent py-3 text-[15px] leading-6 text-foreground outline-none placeholder:text-muted-foreground/80 disabled:opacity-60"
                />
                {voiceSupported && (
                  <button
                    type="button"
                    title={voiceListening ? "Stop voice input" : "Voice input"}
                    aria-label={voiceListening ? "Stop voice input" : "Voice input"}
                    disabled={loading}
                    onClick={() => void toggleVoice()}
                    className={cn(
                      "flex h-12 w-12 shrink-0 items-center justify-center rounded-full transition-colors disabled:opacity-50",
                      voiceListening
                        ? "animate-pulse bg-destructive/15 text-destructive"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground",
                    )}
                  >
                    <Mic className="h-5 w-5" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={loading || !message.trim()}
                  aria-label="Send"
                  className="glowing-ai-send flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-zinc-900 text-white transition-all hover:scale-105 active:scale-95 disabled:scale-100 disabled:bg-muted-foreground/35 dark:bg-white dark:text-zinc-900 dark:disabled:bg-muted-foreground/35 dark:disabled:text-white"
                >
                  <ArrowUp className="h-5 w-5" />
                </button>
              </div>
              {footerNote && <p className="px-2 pt-1 text-[11px] text-muted-foreground">{footerNote}</p>}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export { GlowingAiChatAssistant as FloatingAiAssistant };
